export const learnEn = {
  meta: {
    title: "Learning how to learn — Lumen",
    description: "The interactive guide to understanding how memory, the brain and the best study methods work.",
  },
  header: {
    badge: "Guide",
    title: "Learning how to learn",
    subtitlePre: "You don't need to study more. You need to study ",
    subtitleBold: "better",
    subtitlePost: ". Here's how your mind really works.",
  },
  science: { heading: "The science of studying" },
  methodsHeading: "Study methods",
  finalTip: {
    title: "The final advice",
    body: "There's no perfect method for everyone. Try, measure, adapt. Your AI tutor will help you choose the right method for each subject.",
  },
  lvl: { easy: "Easy", media: "Medium" },
  badge: { accessible: "Accessible" },
  methodsList: { when: "When:" },
  dialog: {
    howTo: "How to do it",
    example: "Practical example",
    adaptationsTitle: "Accessible variants",
    adaptationsFor: "Adaptations for",
    noAdaptations: "No specific variant: the base method is already suitable.",
  },
  adaptMeta: {
    dsa: { label: "SLD", short: "dyslexia, dysorthographia, dyscalculia" },
    adhd: { label: "ADHD", short: "attention and hyperactivity" },
    bes: { label: "SEN", short: "special educational needs" },
    focus: { label: "Focus", short: "difficulty staying focused" },
  },
  chapters: {
    memory: {
      title: "How memory works",
      body: "Memory isn't a drawer: it's a muscle. Every time you RECALL a piece of information — instead of rereading it — you fix it deeper. This is called **Active Recall**.\n\nThe forgetting curve (Ebbinghaus) shows that after 24h we remember 30% of what we've read passively. By reviewing in a distributed way (spaced repetition), retention rises to 80–90%.",
    },
    brain: {
      title: "How the brain works when studying",
      body: "The brain has two modes: **focused** and **diffuse** (relaxed). Alternating them — with breaks and sleep — is what lets learning consolidate. Studying for 8h straight without breaks is counterproductive.",
    },
    mistakes: {
      title: "Most common mistakes",
      body: "• **Rereading** instead of repeating out loud.\n• Studying **passively** with colored underlines without ever recalling.\n• Cramming everything at the last moment.\n• Not sleeping enough: without REM sleep, memory doesn't consolidate.",
    },
    notes: {
      title: "How to take notes",
      body: "The **Cornell** method: divide the page into 3 zones (notes, keywords, summary). At the end of the lesson, close the book and fill in the summary from memory. It increases retention up to 34%.",
    },
    anxiety: {
      title: "How to manage test anxiety",
      body: "4-7-8 breathing (inhale 4s, hold 7s, exhale 8s) lowers cortisol. The day before, don't study new topics: just **review**. Confidence comes from preparation, not from anxiety.",
    },
    forgetting: {
      title: "How to avoid forgetting",
      body: "Review a topic after 1 day, 3 days, 7 days, 21 days. Each recall makes it stronger. It's the secret of medical students.",
    },
  },
  methods: {
    feynman: {
      d: "Explain it as if to a 10-year-old.",
      when: "When you need to really understand a concept.",
      how: [
        "Pick a concept and write it at the top of a page.",
        "Explain it out loud as if talking to a 10-year-old.",
        "Note where you get stuck or use difficult words.",
        "Go back to the book only on those points, then explain again from scratch.",
      ],
      example: "Concept: photosynthesis.\n\nChild-friendly explanation: \"Plants eat light. They take water from the roots and air from the leaves, and with sunlight they cook sugar to grow. The air we breathe is their 'waste'.\"\n\nTypical block: you can't say what chlorophyll has to do with it. → You go back to the book, discover it's the green molecule that captures light, and explain again: \"Chlorophyll is like a solar panel inside the leaf.\"",
      tip: "If you can't explain it simply, you haven't understood it. Yet.",
      adaptations: {
        dsa: [
          "Record the explanation by voice instead of writing it — you can listen back.",
          "Use the app's AI tutor to rewrite the concept in simpler words.",
          "Use a high-readability font and enable SLD mode in settings.",
        ],
        adhd: [
          "One concept at a time, with a visible 10-minute timer.",
          "Explain while walking or gesturing: movement helps maintain focus.",
          "Immediate reward at the end (5 min music, snack, break).",
        ],
        bes: [
          "Use drawings or icons instead of difficult words.",
          "Ask a friend/parent to listen to you: feedback reduces anxiety.",
          "Split the explanation into 3 mini-parts of 2 minutes each.",
        ],
        focus: [
          "Remove distractions: phone in another room, notifications off.",
          "Explain out loud as if making a video: it triggers more attention.",
        ],
      },
    },
    activeRecall: {
      d: "Close the book. Repeat out loud.",
      when: "Always. It's method #1.",
      how: [
        "Read a paragraph or a page.",
        "Close the book.",
        "Repeat aloud (or write down) everything you remember.",
        "Reopen it and correct mistakes.",
      ],
      example: "History — chapter on the French Revolution.\n\n1) Read the causes (1789).\n2) Close the book and try to say: \"Economic crisis, inequality among the three estates, influence of the Enlightenment, storming of the Bastille on July 14th…\"\n3) Reopen it: you forgot the famine of 1788. Mark it in red.\n4) Try again after 10 minutes — this time you remember it.",
      tip: "Rereading ≠ studying. If you can't recall it, you don't know it.",
      adaptations: {
        dsa: [
          "Repeat out loud instead of writing: it bypasses tiring reading and writing.",
          "Use audio flashcards: record the question and listen before answering.",
          "Ask the AI tutor to ask you oral questions about what you've studied.",
        ],
        adhd: [
          "Short 5-10 minute sessions, then switch topic to avoid boredom.",
          "Turn review into a point-scoring game with yourself.",
          "Standing or walking: movement helps recall.",
        ],
        bes: [
          "Start with 3 simple questions before increasing difficulty.",
          "Use guide images to trigger recall (visual memory).",
        ],
        focus: [
          "Short timer (10 min) and a clear goal: \"recall 5 points\".",
          "Write the answer right away without rereading: it activates recall immediately.",
        ],
      },
    },
    pomodoro: {
      d: "25 min focus + 5 min break.",
      when: "To start studying without procrastinating.",
      how: [
        "Choose ONE precise task (not 'study history', but 'read pages 45–52').",
        "25 min timer. Phone in another room.",
        "5 min real break (stand up, drink, look outside).",
        "After 4 pomodoros: a long break of 15–30 min.",
      ],
      example: "3:00 PM — Pomodoro 1: read pages 45–52 of history.\n3:25 PM — Break: walk in the kitchen, drink water.\n3:30 PM — Pomodoro 2: close the book and do Active Recall of the chapter.\n3:55 PM — Break.\n4:00 PM — Pomodoro 3: solve 5 math exercises.\n4:25 PM — Long break, 20 min.",
      tip: "Use the app's Study timer → Pomodoro mode.",
      adaptations: {
        dsa: [
          "During the pomodoro alternate 10 min reading + 15 min oral/visual activity.",
          "During the break don't read: rest your eyes (look far away, natural light).",
        ],
        adhd: [
          "Reduce to **15 min focus + 5 min break** — more realistic for ADHD.",
          "Visible and audible timer. Write the task on a post-it in front of you.",
          "If you get distracted, don't judge yourself: notice the thought, go back to the task.",
        ],
        bes: [
          "Start with just 1 pomodoro a day, then increase gradually.",
          "Use an analog timer or hourglass: time becomes concrete.",
        ],
        focus: [
          "Airplane mode mandatory during the 25 minutes.",
          "REAL break: no social media, no video. Just movement or water.",
        ],
      },
    },
    spacedRepetition: {
      d: "Review at growing intervals.",
      when: "To remember in the long term.",
      how: [
        "Study today (day 0).",
        "Review after 1 day.",
        "Then after 3 days, 7 days, 21 days.",
        "Every time you get it wrong, restart from 1 day.",
      ],
      example: "English vocabulary, 20 new words on Monday.\n\n• Tue (day 1): recall the 20 words. You miss 4 → flag them.\n• Thu (day 3): recall all of them. Move the 4 missed ones back to day 1.\n• Following Mon (day 7): review only the stable ones.\n• In 3 weeks: final review, 5 minutes.\n\nResult: 90% retention after a month, vs. 30% with rereading alone.",
      tip: "The app's flashcards already use this method (5 Leitner boxes).",
      adaptations: {
        dsa: [
          "Prefer flashcards with images or audio, not just text.",
          "In the app you can create voice flashcards: record the answer by voice.",
        ],
        adhd: [
          "Max 10 cards per session: small decks prevent giving up.",
          "Always review at the same time: routine helps more than willpower.",
        ],
        bes: [
          "Use different colors per subject: it helps find the right cards.",
          "Ask for support creating the first cards, then it becomes independent.",
        ],
        focus: [
          "10 minutes in the morning right after waking up: the brain is more receptive.",
          "Close all other tabs/apps before starting the deck.",
        ],
      },
    },
    cornellNotes: {
      d: "Notes in 3 zones: notes / keywords / summary.",
      when: "During lectures.",
      how: [
        "Divide the page into 3 zones: left column (keywords), right column (notes), bottom band (summary).",
        "During the lesson write only in the notes column (right).",
        "Right after, fill in the keywords on the left.",
        "By evening, write 2-3 lines of summary at the bottom.",
      ],
      example: "Biology page.\n\n[Right notes] Mitosis has 4 phases: prophase, metaphase, anaphase, telophase…\n[Left words] mitosis · 4 phases · chromosomes\n[Bottom summary] Mitosis divides one cell into 2 identical ones. 4 phases. Used for growth and repair.",
      tip: "By covering the right column you can use the keywords as flashcards.",
      adaptations: {
        dsa: [
          "Write in block letters or use a tablet with a high-readability font.",
          "Record the lesson (with permission) and fill in notes afterward, calmly.",
          "In the left column use symbols/icons instead of words when you can.",
        ],
        adhd: [
          "Focus only on the right column during the lesson: one thing at a time.",
          "Fill in keywords and summary RIGHT after the lesson, not the day after.",
        ],
        bes: [
          "Pre-printed template with the 3 zones already divided: reduces organizational effort.",
          "Summary in max 2 sentences: better short and completed than long and left half-done.",
        ],
        focus: [
          "Pen always in hand: writing keeps attention even if you don't copy everything.",
        ],
      },
    },
    blurting: {
      d: "Write everything you remember, then compare.",
      when: "Before a test.",
      how: [
        "Choose a topic.",
        "Blank sheet. 5-10 min: write EVERYTHING you remember, in no particular order.",
        "Open the book and compare.",
        "Circle the gaps in red. They are your next study session.",
      ],
      example: "Topic: circulatory system.\n\nBlurt: \"Heart, 4 chambers, right and left atrium, ventricles, arteries carry oxygenated blood except pulmonary, veins…\"\n\nOpen the book: you forgot the tricuspid valve and the role of capillaries. That's what to review tonight.",
      tip: "Blurting makes gaps visible in 10 minutes. Do it the day before a test.",
      adaptations: {
        dsa: [
          "Do the blurting out loud: record audio instead of writing.",
          "When comparing with the book, use colored highlighters for the \"gaps\".",
        ],
        adhd: [
          "5-minute timer: when it ends, stop, even if you haven't finished.",
          "One topic at a time. Don't move to the next before comparing.",
        ],
        bes: [
          "You can use diagrams/arrows instead of full sentences.",
          "The first blurt is always full of gaps: that's NORMAL, not a failure.",
        ],
        focus: [
          "Blank sheet in front, everything else far away. Write in one go without stopping.",
        ],
      },
    },
    mindMapping: {
      d: "Represent concepts visually.",
      when: "To connect complex ideas.",
      how: [
        "Write the central concept in the middle of a page.",
        "Branch out with 3-5 main branches (subtopics).",
        "Each branch splits into sub-branches with details, examples, dates.",
        "Use different colors for macro-areas and arrows for connections.",
      ],
      example: "Center: \"World War II\".\n\nBranches: Causes · Fronts · People · Consequences.\n\n• Causes → Treaty of Versailles, 1929 crisis, rise of Nazism.\n• Fronts → Europe, Pacific, Africa.\n• People → Hitler, Churchill, Roosevelt, Stalin.\n• Consequences → UN, Cold War, decolonization.\n\nArrows between '1929 crisis' and 'rise of Nazism' to show cause-effect.",
      tip: "In the app you can generate mind maps from your notes in the Study section.",
      adaptations: {
        dsa: [
          "IDEAL method for SLD: leverages visual memory and reduces text.",
          "Use colors, icons and drawings instead of long sentences.",
          "In the app you can generate the map from your notes in one click.",
        ],
        adhd: [
          "Start from the center without planning too much: add branches as you think.",
          "If you get lost, stop everything and go back to the central concept.",
        ],
        bes: [
          "Small map, max 3 main branches. Better simple and clear.",
          "One color per branch helps memorize the connections.",
        ],
        focus: [
          "Build the map by hand on a large sheet: the gesture keeps you awake.",
          "Make one map at a time per topic — don't mix subjects.",
        ],
      },
    },
  },
  toast: {
    mustLogin: "You must be logged in to save",
    saved: "Result saved to your profile ✨",
  },
  quiz: {
    cta: {
      discoverTitle: "Discover your method",
      discoverSubtitle: "7 quick questions to find out which study method works best for you.",
      yourMethod: "Your method",
      recommendedBadge: "Recommended",
      savedOn: "saved on {{date}}",
      thenPrefix: " · then ",
      andWord: " and ",
      viewCard: "View card",
      retakeTest: "Retake the test",
    },
    dialogTitle: "Discover your method",
    questionOf: "Question {{n}} of {{total}}",
    resultsSubtitle: "Here are the methods best suited to you",
    noResults: "No results — try again.",
    recommendedBadge: "Recommended",
    retake: "Retake the test",
    questions: [
      {
        q: "When you study something new, what helps you understand it most?",
        options: [
          "Explaining it out loud to someone (or myself)",
          "Seeing diagrams, connections and drawings",
          "Repeating aloud without looking at the book",
        ],
      },
      {
        q: "How hard is it for you to get started studying?",
        options: [
          "A lot, I always procrastinate",
          "Quite a bit, after a while I get into the flow",
          "Not much, I start right away",
        ],
      },
      {
        q: "How do you take notes in class?",
        options: [
          "I write practically everything",
          "Only keywords",
          "I listen, rewrite at home",
        ],
      },
      {
        q: "After how long do you tend to forget what you've studied?",
        options: [
          "After a few days",
          "After a week or two",
          "I remember it for a long time",
        ],
      },
      {
        q: "How do you usually prepare for a test?",
        options: [
          "Intensive review the day before",
          "A little review at a time during the week",
          "I study everything the night before",
        ],
      },
      {
        q: "Which activity helps you fix information the most?",
        options: [
          "Writing by hand",
          "Speaking, explaining",
          "Drawing maps, diagrams, colors",
        ],
      },
      {
        q: "What kind of subjects weigh on you the most?",
        options: [
          "Definitions, vocabulary, formulas",
          "Abstract concepts to understand",
          "Connected topics (history, literature)",
        ],
      },
    ],
  },
};
